import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import * as dateFns from 'date-fns';
import * as _ from 'lodash';
import {
	electricityAllowedConsumptions,
	eletricityConsumptionTypes_BTN_SimplePT,
	eletricityConsumptionTypes_BTNPT,
	eletricityConsumptionTypesES,
	eletricityConsumptionTypesPT,
	naturalGasConsumptionTypesES,
	naturalGasConsumptionTypesPT,
} from 'src/app/constants';
import { UsersService } from 'src/app/services/users.service';
import { DatePickerType } from 'is-lib';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ConsumptionsFormGroup, ElectricityEquipmentFormGroup, NaturalGasEquipmentFormGroup } from 'src/app/components/energy-meters/energy-meters-types';
import { merge, combineLatest } from 'rxjs';
import { eachMonthOfInterval, isSameMonth, startOfMonth, subMonths } from 'date-fns';
import { left } from '@popperjs/core';

@Component({
	selector: 'new-app-consumptions',
	templateUrl: './new-consumptions.component.html',
	styleUrls: ['./new-consumptions.component.scss'],
	animations: [
		trigger('dotState', [
			state('dot-valid', style({ transform: 'scale(1)', opacity: 1 })),
			state('dot-error', style({ transform: 'scale(1)', opacity: 1 })),
			state('dot-empty', style({ transform: 'scale(1)', opacity: 1 })),
			state('dot-incomplete', style({ transform: 'scale(1)', opacity: 1 })),
			transition('* => *', [
				style({ transform: 'scale(1.2)', opacity: 0.5 }),
				animate('150ms ease-out'),
			]),
		]),
	],
})
export class NewConsumptionsComponent implements OnInit, OnChanges {
	@Input() consumptionsFormArray: FormArray<FormGroup<ConsumptionsFormGroup>> =
		new FormArray<FormGroup<ConsumptionsFormGroup>>([]);
	@Input() allowsPriceInput: boolean = true;
	@Input() singleMonthConsumptionInput: boolean = false;
	@Input() isElectricity!: boolean;

	possibleConsumptionInputs?: Map<string, string> = undefined;
	possiblePriceInputs?: Map<string, string> = undefined;

	monthRange: Date[];
	sumTotal = 0;
	priceSumTotal = 0;

	datepickerType = DatePickerType.month;

	parentFormGroup?: FormGroup<any>;
	singleMonthInputFormGroup?: FormGroup<ConsumptionsFormGroup>;

	constructor(private _user: UsersService) {
		let endDate = subMonths(startOfMonth(new Date()), 2);
		let startDate = subMonths(endDate, 11);

		this.monthRange = eachMonthOfInterval({
			start: startDate,
			end: endDate,
		});
	}

	ngOnInit(): void {
		if (this.isElectricity) {
			this.parentFormGroup = this.consumptionsFormArray.parent as FormGroup<ElectricityEquipmentFormGroup>;
			
			this.getPossiblePriceAndConsumptionInputs();

			merge(
				(this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>).controls.tensionLevel.controls.finalValue.valueChanges,
				(this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>).controls.countType.controls.finalValue.valueChanges
			).subscribe(() => {
				this.getPossiblePriceAndConsumptionInputs();
			});

		} else {
			this.parentFormGroup = this.consumptionsFormArray
				.parent as FormGroup<NaturalGasEquipmentFormGroup>;
		}

		if (this.singleMonthConsumptionInput) {
			this.singleMonthInputFormGroup = new FormGroup<ConsumptionsFormGroup>({
				date: new FormControl<Date | undefined>(undefined, {
					nonNullable: true,
				}),
				offPeak: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
					validators: this.isPossibleInput('offPeak')
						? Validators.required
						: null,
				}),
				peak: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
					validators: this.isPossibleInput('peak') ? Validators.required : null,
				}),
				standard: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
					validators: this.isPossibleInput('standard')
						? Validators.required
						: null,
				}),
				superOffPeak: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
					validators: this.isPossibleInput('superOffPeak')
						? Validators.required
						: null,
				}),
				gas: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
					validators: this.isPossibleInput('gas') ? Validators.required : null,
				}),
				id: new FormControl<number | undefined>(undefined, {
					nonNullable: true,
				}),
				singleLine: new FormControl<boolean>(true, { nonNullable: true }),
			});

			this.consumptionsFormArray.controls.push(this.singleMonthInputFormGroup);

			this.singleMonthInputFormGroup.valueChanges.subscribe(() => {
				this.calculateSums();
			});
		} else if (
			this.consumptionsFormArray.controls.filter(
				(group: FormGroup<ConsumptionsFormGroup>) =>
					!group.controls.singleLine.value
			).length < this.monthRange.length
		) {
			
			this.monthRange.forEach( (month: Date) => {
				let monthWithConsumption = this.consumptionsFormArray.controls.find( consumptionFormGroup => isSameMonth(month, new Date(consumptionFormGroup.controls.date.value ?? 0)) );

				if (!monthWithConsumption) {
					const newFormGroup = new FormGroup<ConsumptionsFormGroup>({
						date: new FormControl<Date>(month, { nonNullable: true }),
						offPeak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('offPeak')
								? Validators.required
								: null,
						}),
						peak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('peak')
								? Validators.required
								: null,
						}),
						standard: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('standard')
								? Validators.required
								: null,
						}),
						superOffPeak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('superOffPeak')
								? Validators.required
								: null,
						}),
						gas: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('gas')
								? Validators.required
								: null,
						}),
						id: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
						}),
						singleLine: new FormControl<boolean>(false, { nonNullable: true }),
					});
					
					newFormGroup.valueChanges.subscribe(() => {
						this.calculateSums();
					});
					
					this.consumptionsFormArray.push(newFormGroup)
				}
			});
		}

		this.parentFormGroup.valueChanges.subscribe(() => {
			this.calculateSums();
		});

		this.calculateSums();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes['consumptionsFormArray']?.previousValue?.parent.controls.equipmentCode.controls.finalValue.value != changes['consumptionsFormArray']?.currentValue.parent.controls.equipmentCode.controls.finalValue.value
		) {
			this.parentFormGroup = this.consumptionsFormArray.parent as FormGroup<ElectricityEquipmentFormGroup>;
			
			this.getPossiblePriceAndConsumptionInputs();

			this.monthRange.forEach( (month: Date) => {
				let monthWithConsumption = this.consumptionsFormArray.controls.find( consumptionFormGroup => isSameMonth(month, new Date(consumptionFormGroup.controls.date.value ?? 0)) );

				if (!monthWithConsumption) {
					const newFormGroup = new FormGroup<ConsumptionsFormGroup>({
						date: new FormControl<Date>(month, { nonNullable: true }),
						offPeak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('offPeak')
								? Validators.required
								: null,
						}),
						peak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('peak')
								? Validators.required
								: null,
						}),
						standard: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('standard')
								? Validators.required
								: null,
						}),
						superOffPeak: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('superOffPeak')
								? Validators.required
								: null,
						}),
						gas: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
							validators: this.isPossibleInput('gas')
								? Validators.required
								: null,
						}),
						id: new FormControl<number | undefined>(undefined, {
							nonNullable: true,
						}),
					singleLine: new FormControl<boolean>(false, { nonNullable: true }),
					});
					
					newFormGroup.valueChanges.subscribe(() => {
						this.calculateSums();
					});
					
					this.consumptionsFormArray.push(newFormGroup)
				}
			});
		}

		if (
			changes['singleMonthConsumptionInput']?.previousValue != changes['singleMonthConsumptionInput']?.currentValue &&
			!changes['singleMonthConsumptionInput'].isFirstChange()
		) {
			if (changes['singleMonthConsumptionInput'].currentValue) {
				if (this.singleMonthInputFormGroup == null) {
					this.singleMonthInputFormGroup = new FormGroup<ConsumptionsFormGroup>(
						{
							date: new FormControl<Date | undefined>(undefined, {
								nonNullable: true,
							}),
							offPeak: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
								validators: this.isPossibleInput('offPeak')
									? Validators.required
									: null,
							}),
							peak: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
								validators: this.isPossibleInput('peak')
									? Validators.required
									: null,
							}),
							standard: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
								validators: this.isPossibleInput('standard')
									? Validators.required
									: null,
							}),
							superOffPeak: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
								validators: this.isPossibleInput('superOffPeak')
									? Validators.required
									: null,
							}),
							gas: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
								validators: this.isPossibleInput('gas')
									? Validators.required
									: null,
							}),
							id: new FormControl<number | undefined>(undefined, {
								nonNullable: true,
							}),
							singleLine: new FormControl<boolean>(true, { nonNullable: true }),
						}
					);

					this.consumptionsFormArray.controls.push(
						this.singleMonthInputFormGroup
					);

					this.singleMonthInputFormGroup.valueChanges.subscribe(() => {
						this.calculateSums();
					});
				}

				this.consumptionsFormArray.controls.forEach((fg) => {
					for (const key in fg.controls) {
						if (this.isPossibleInput(key) && fg.controls.singleLine.value) {
							fg.get(key)?.addValidators(Validators.required);
						} else {
							fg.get(key)?.clearValidators();
						}
					}
				});
			} else {
				this.consumptionsFormArray.controls.forEach((fg) => {
					for (const key in fg.controls) {
						if (this.isPossibleInput(key) && !fg.controls.singleLine.value) {
							fg.get(key)?.addValidators(Validators.required);
						} else {
							fg.get(key)?.clearValidators();
						}
					}
				});
			}
			this.consumptionsFormArray.updateValueAndValidity();
			this.calculateSums();
		}
	}

	getPossiblePriceAndConsumptionInputs() {
		const tensionLevel = (this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>).controls.tensionLevel.controls.finalValue.value?.toUpperCase();
		let countType = (this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>).controls.countType.controls.finalValue.value?.toUpperCase();
		const timeCycle = (this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>).controls.timeCycle.controls.finalValue.value?.toUpperCase();
		
		const userCountry = this._user.userCountryCode.toLocaleUpperCase();

		if (countType == 'SIMPLE') {
			countType = 'SIMPLES';
		}

		if (tensionLevel) {
			let tensionLevelOptions =  electricityAllowedConsumptions[tensionLevel];

			if (Object.hasOwn(tensionLevelOptions, 'SIMPLES') && countType) {
				this.possibleConsumptionInputs = tensionLevelOptions[countType].consumptionTypes[userCountry];
				this.possiblePriceInputs = tensionLevelOptions[countType].energyPrices[userCountry];
			} else if (timeCycle) {
				this.possibleConsumptionInputs = tensionLevelOptions.consumptionTypes[userCountry];
				this.possiblePriceInputs = tensionLevelOptions.energyPrices[userCountry];
			} else {
				this.possibleConsumptionInputs = undefined;
				this.possiblePriceInputs = undefined;
			}

		} else {
			this.possibleConsumptionInputs = undefined;
			this.possiblePriceInputs = undefined;
		}
	}

	protected copyPasteHandler(
		monthIndex: number,
		inputIndex: number,
		event: ClipboardEvent
	) {
		event.preventDefault();

		let regExPattern = new RegExp('(\\t|\\n\\r|\\r\\n|\\n|\\r)', 'g');
		let pastedData = event.clipboardData?.getData('text/plain');
		let treatedPastedData = pastedData
			?.replaceAll(regExPattern, ';')
			.split(';');
		let monthlyConsumptions: FormGroup<ConsumptionsFormGroup>[] =
			this.consumptionsFormArray.controls;

		let possibleInputs: string[] = this.possibleConsumptionInputValues;

		if (treatedPastedData) {
			let monthCounter: number = monthIndex;
			let inputCounter: number = inputIndex;

			treatedPastedData.forEach((pasteValue: string) => {
				if (monthCounter < monthlyConsumptions.length) {
					let currentInput: string = possibleInputs[inputCounter];

					monthlyConsumptions[monthCounter]
						.get(currentInput)
						?.setValue(+pasteValue);

					inputCounter += 1;

					if (inputCounter == possibleInputs.length) {
						inputCounter = 0;
						monthCounter += 1;
					}
				}
			});
		}
	}

	canInputConsumptions(): boolean { 
		return this.possibleConsumptionInputs !== undefined && this.possiblePriceInputs !== undefined;
	}

	protected calculateSums() {
		let arrayToSum;
		if (this.singleMonthConsumptionInput) {
			arrayToSum = this.consumptionsFormArray
				.getRawValue()
				.filter((consumption) => consumption.singleLine);
		} else {
			arrayToSum = this.consumptionsFormArray
				.getRawValue()
				.filter((consumption) => !consumption.singleLine);
		}

		this.sumTotal = arrayToSum.reduce<number>((sum, obj) => {
			let totalFromObject = Object.entries(obj)
				.filter(
					([key, value]) =>
						this.possibleConsumptionInputValues.includes(key) &&
						_.isFinite(value)
				)
				.map(([key, value]) => (value ?? 0) as number)
				.reduce<number>((sum, value) => (sum += value), 0);
			return sum + totalFromObject;
		}, 0);

		let formGroupValues = null;

		if (this.isElectricity) {
			formGroupValues = this.parentFormGroupAsElectricity.getRawValue();
		} else {
			formGroupValues = this.parentFormGroupAsNaturalGas.getRawValue();
		}

		this.priceSumTotal = Object.entries(formGroupValues)
			.filter(
				([key, value]) =>
					this.possiblePriceInputValues.includes(key) && _.isFinite(value)
			)
			.map(([key, value]) => (value ?? 0) as number)
			.reduce<number>((sum, value) => sum + value, 0);
	}

	isMonthValid(form: FormGroup): boolean {
		return this.possibleConsumptionInputValues.every((key) => {
			const control = form.get(key);
			return control && control.valid && control.value != null;
		});
	}

	isMonthWithErrors(form: FormGroup): boolean {
		return this.possibleConsumptionInputValues.some((key) => {
			const control = form.get(key);
			return control && control.invalid && control.touched;
		});
	}

	isMonthEmpty(form: FormGroup): boolean {
		return this.possibleConsumptionInputValues.every((key) => {
			const control = form.get(key);
			return (
				!control ||
				control.value === null ||
				control.value === undefined ||
				control.value === ''
			);
		});
	}

	getMonthDotClass(form: FormGroup): string {
		if (!form) return 'dot-empty';

		const hasValue = this.possibleConsumptionInputValues.some((key) => {
			const control = form.get(key);
			return control?.value != null && control.value !== '';
		});

		const hasErrors = this.possibleConsumptionInputValues.some((key) => {
			const control = form.get(key);
			const validator = control?.validator;
			if (!control || !validator) return false;

			// Manually validate even if control is disabled
			const errors = validator(control);
			return errors != null && control.touched;
		});

		const allValid = this.possibleConsumptionInputValues.every((key) => {
			const control = form.get(key);
			if (!control) return false;
			if (control.disabled) {
				// Manually run validator if exists
				const validator = control.validator;
				if (!validator) return true;
				const errors = validator(control);
				return errors == null && control.value != null && control.value !== '';
			} else {
				return control.valid && control.value != null && control.value !== '';
			}
		});

		if (allValid) return 'dot-valid';
		if (hasErrors) return 'dot-error';
		if (hasValue) return 'dot-incomplete';
		return 'dot-empty';
	}

	getValidMonthCount(): number {
		return this.consumptionsFormArray.controls.filter(
			(fg) => this.getMonthDotClass(fg) === 'dot-valid'
		).length;
	}

	getMonthTooltip(form: FormGroup): string {
		const date = form.get('date')?.value;
		const label = date
			? new Date(date + 'Z').toLocaleDateString('pt-PT', {
				month: 'short',
				year: 'numeric',
			})
			: '';
		if (this.isMonthValid(form)) return `${label} - Válido`;
		if (this.isMonthWithErrors(form)) return `${label} - Erro`;
		return `${label} - Incompleto`;
	}

	clearMonth(form: FormGroup): void {
		this.possibleConsumptionInputValues.forEach((key) => {
			form.get(key)?.setValue(null);
			form.get(key)?.markAsUntouched();
		});
	}

	scrollToMonth(index: number): void {
		const el = document.getElementById('month-row-' + index);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
			el.classList.add('highlighted');
			setTimeout(() => el.classList.remove('highlighted'), 1000);
		}
	}

	getGridTemplateStyle(isPriceRow: boolean): { [klass: string]: any } {
		const count = this.possibleConsumptionInputValues?.length || 1;
		const px = isPriceRow ? '100px' : '90px';

		return {
			'grid-template-columns': `140px repeat(${count}, 1fr) ${px}`,
			gap: '12px',
		};
	}

	protected get parentFormGroupAsElectricity(): FormGroup<ElectricityEquipmentFormGroup> {
		return this.parentFormGroup as FormGroup<ElectricityEquipmentFormGroup>;
	}

	protected get parentFormGroupAsNaturalGas(): FormGroup<NaturalGasEquipmentFormGroup> {
		return this.parentFormGroup as FormGroup<NaturalGasEquipmentFormGroup>;
	}

	protected get possibleConsumptionInputKeys(): string[] {
		return Array.from(this.possibleConsumptionInputs?.keys() ?? []);
	}

	protected get possibleConsumptionInputValues(): string[] {
		return Array.from(this.possibleConsumptionInputs?.values() ?? []);
	}

	protected get possiblePriceInputValues(): string[] {
		return Array.from(this.possiblePriceInputs?.values() ?? []);
	}

	protected isPossibleInput(keyName: string): boolean {
		return this.possibleConsumptionInputs?.has(keyName) ?? false;
	}

	getMonthlyTotal(index: number): number {
		const fg = this.consumptionsFormArray.at(index);
		return this.possibleConsumptionInputValues
			.map((key) => fg.get(key)?.value || 0)
			.reduce((sum, val) => sum + val, 0);
	}
}
